/**
 * TemplateSelector — Browse and pick from the KPI template library.
 *
 * Groups templates by category. Click a template to create a KPI definition
 * from it, or create a custom one from scratch.
 */
import React, { useEffect, useState, useMemo } from 'react';
import Search from 'lucide-react/dist/esm/icons/search';
import Plus from 'lucide-react/dist/esm/icons/plus';
import BookOpen from 'lucide-react/dist/esm/icons/book-open';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right';
import type { KPIDefinition } from '../../../services/api/kpi';
import { useKPIStore } from '../../../stores/kpiStore';
import { useUIStore } from '../../../stores/uiStore';
import { getTranslation } from '../../../i18n';

interface TemplateSelectorProps {
  onSelectTemplate: (template: KPIDefinition) => void;
  onCreateCustom: () => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  onSelectTemplate,
  onCreateCustom,
}) => {
  const { templates, templatesLoading, fetchTemplates } = useKPIStore();
  const { language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);

  const CATEGORY_ICONS: Record<string, { emoji: string; label: string }> = {
    financial: { emoji: '💰', label: t('perfCategoryFinancial') },
    sales: { emoji: '📈', label: t('perfCategorySales') },
    marketing: { emoji: '📢', label: t('perfCategoryMarketing') },
    operations: { emoji: '⚙️', label: t('perfCategoryOperations') },
    product: { emoji: '🚀', label: t('perfCategoryProduct') },
    hr: { emoji: '👥', label: t('perfCategoryHrPeople') },
    customer_success: { emoji: '🤝', label: t('perfCategoryCustomerSuccess') },
  };
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Group by category
  const grouped = useMemo(() => {
    const groups: Record<string, KPIDefinition[]> = {};
    for (const t of templates) {
      const cat = t.category || 'custom';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(t);
    }
    return groups;
  }, [templates]);

  // Filter by search and category
  const filtered = useMemo(() => {
    let result = templates;
    if (selectedCategory) {
      result = result.filter(t => t.category === selectedCategory);
    }
    const q = search.toLowerCase();
    if (q) {
      result = result.filter(t =>
        t.name.toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.business_question || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [templates, search, selectedCategory]);

  const categories = Object.keys(grouped).sort();

  if (templatesLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-status-success/30 border-t-success rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen size={18} className="text-primary" />
          <h3 className="text-base font-semibold text-content">{t('perfCatalogTitle')}</h3>
          <span className="text-xs text-content-tertiary ml-1">{t('perfCatalogTemplatesCount').replace('{{count}}', String(templates.length))}</span>
        </div>
        <button
          onClick={onCreateCustom}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-status-success/10 text-status-success text-xs font-medium hover:bg-status-success/20 transition-colors border border-status-success/20 cursor-pointer"
        >
          <Plus size={14} />
          {t('perfCatalogBtnCustomKpi')}
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-faint" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('perfCatalogSearchPlaceholder')}
          className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-surface-primary border border-subtle text-content text-xs placeholder-content-muted outline-none focus:border-brand/30 transition-all"
        />
      </div>

      {/* Category Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all cursor-pointer border outline-none
            ${!selectedCategory
              ? 'bg-brand/10 text-primary border-brand/20'
              : 'bg-transparent text-content-muted border-subtle hover:bg-surface-primary'
            }
          `}
        >
          {t('perfCatalogFilterAll')}
        </button>
        {categories.map(cat => {
          const info = CATEGORY_ICONS[cat];
          const isActive = selectedCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => setSelectedCategory(isActive ? null : cat)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all cursor-pointer border outline-none
                ${isActive
                  ? 'bg-brand/10 text-primary border-brand/20'
                  : 'bg-transparent text-content-muted border-subtle hover:bg-surface-primary'
                }
              `}
            >
              {info ? `${info.emoji} ${info.label}` : cat}
              <span className="ml-1 text-content-muted">{grouped[cat].length}</span>
            </button>
          );
        })}
      </div>

      {/* Template Cards */}
      <div className="grid gap-2">
        {filtered.map(tmpl => (
          <button
            key={tmpl.id}
            type="button"
            onClick={() => onSelectTemplate(tmpl)}
            className="w-full text-left p-4 rounded-xl bg-surface-primary border border-subtle hover:border-brand/25 hover:bg-brand/[0.04] transition-all cursor-pointer outline-none group"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-content">{tmpl.name}</span>
                  <span className="text-[10px] text-content-muted px-1.5 py-0.5 rounded bg-surface-secondary">
                    {(tmpl.category || '').replace('_', ' ')}
                  </span>
                </div>
                {tmpl.description && (
                  <p className="text-xs text-content-muted line-clamp-2 mb-1.5">{tmpl.description}</p>
                )}
                {tmpl.business_question && (
                  <p className="text-[11px] text-brand/60 italic">
                    <Sparkles size={10} className="inline mr-1" />
                    {tmpl.business_question}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2 text-[11px] text-content-muted">
                  {tmpl.unit_label && <span>{t('perfCatalogTemplateUnit')} {tmpl.unit_label}</span>}
                  {tmpl.default_target !== null && <span>{t('perfCatalogTemplateTarget')} {tmpl.default_target}{tmpl.unit_label}</span>}
                  <span>{t('perfCatalogTemplateCadence')} {tmpl.cadence}</span>
                  <span className="capitalize">{tmpl.directionality?.replace('_', ' ')}</span>
                </div>
              </div>
              <ArrowRight size={14} className="text-content-faint group-hover:text-brand/50 transition-colors mt-1 shrink-0" />
            </div>
          </button>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <BookOpen size={28} className="text-content-faint mx-auto mb-3" />
            <p className="text-sm text-content-muted">{t('perfCatalogNoTemplatesMatch')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(TemplateSelector);
